import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import NumberInput from '../../Components/NumberInput';
import { INTERVAL_UNITS, BuildingCheckTemplate } from '../BuildingChecks/types';
import { DateInterval, DateIntervalUnit } from '../../Helpers/DateUtils';
import {
    ASSESSMENT_GROUP_KEYS,
    ASSESSMENT_GROUPS,
    AssessmentGroupKey,
    DEFAULT_ASSESSMENT_INTERVAL
} from '../../Helpers/assessmentGroups';
import { buildingChecksApi, AssessmentTemplates } from '../../api/buildingChecksApi';
import { useIsAdmin } from '../../hooks/useIsAdmin';

interface EditableTemplate {
    interval: DateInterval;
    description: string;
}

/**
 * Settings "Skickbedömning" tab.
 *
 * Edits the interval and description of the three Skickbedömning system
 * templates (`inspections/templates/conditionAssessment_{group}`). While a
 * template is missing its card shows the code defaults read-only and the tab
 * offers the same "Skapa mallar" action as Konfigurationsstatus (D5, D28).
 * Nothing is written on open.
 */
const ConditionAssessmentTab: React.FC = () => {
    const isAdmin = useIsAdmin();
    const [templates, setTemplates] = useState<AssessmentTemplates>({});
    const [edits, setEdits] = useState<Partial<Record<AssessmentGroupKey, EditableTemplate>>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

    const loadTemplates = useCallback(async () => {
        try {
            setLoading(true);
            const loaded = await buildingChecksApi.loadAssessmentTemplates();
            setTemplates(loaded);
            const nextEdits: Partial<Record<AssessmentGroupKey, EditableTemplate>> = {};
            ASSESSMENT_GROUP_KEYS.forEach(group => {
                const template = loaded[group];
                if (template) {
                    nextEdits[group] = { interval: template.interval, description: template.description || '' };
                }
            });
            setEdits(nextEdits);
        } catch (error) {
            console.error('Error loading condition assessment templates:', error);
            setAlert({ type: 'danger', message: 'Fel vid laddning av mallar för skickbedömning' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const missingGroups = ASSESSMENT_GROUP_KEYS.filter(group => !templates[group]);

    const handleCreateTemplates = async () => {
        try {
            setCreating(true);
            setAlert(null);
            const created = await buildingChecksApi.ensureAssessmentTemplates();
            setAlert({
                type: 'success',
                message: created.length > 0
                    ? `${created.length} ${created.length === 1 ? 'mall' : 'mallar'} för skickbedömning skapade`
                    : 'Alla mallar för skickbedömning finns redan'
            });
            await loadTemplates();
        } catch (error) {
            console.error('Error creating condition assessment templates:', error);
            setAlert({ type: 'danger', message: error instanceof Error ? error.message : 'Kunde inte skapa mallarna' });
        } finally {
            setCreating(false);
        }
    };

    const saveTemplates = async () => {
        try {
            setSaving(true);
            setAlert(null);
            for (const group of ASSESSMENT_GROUP_KEYS) {
                const template = templates[group];
                const edit = edits[group];
                if (!template || !edit) continue;
                const unchanged = template.interval.value === edit.interval.value
                    && template.interval.unit === edit.interval.unit
                    && (template.description || '') === edit.description;
                if (unchanged) continue;
                await buildingChecksApi.updateAssessmentTemplate(group, {
                    interval: { value: Math.max(1, Math.round(edit.interval.value)), unit: edit.interval.unit },
                    description: edit.description
                });
            }
            setAlert({ type: 'success', message: 'Mallarna för skickbedömning har sparats' });
            await loadTemplates();
        } catch (error) {
            console.error('Error saving condition assessment templates:', error);
            setAlert({ type: 'danger', message: error instanceof Error ? error.message : 'Fel vid sparande av mallar för skickbedömning' });
        } finally {
            setSaving(false);
        }
    };

    const updateEdit = (group: AssessmentGroupKey, patch: Partial<EditableTemplate>) => {
        setEdits(prev => {
            const current = prev[group];
            if (!current) return prev;
            return { ...prev, [group]: { ...current, ...patch } };
        });
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Laddar...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="condition-assessment-tab">
            {alert && (
                <Alert
                    variant={alert.type}
                    dismissible
                    onClose={() => setAlert(null)}
                    className="mb-4"
                >
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h5 className="mb-1">Skickbedömning</h5>
                    <p className="text-muted mb-0">
                        Tre systemmallar schemalägger skickbedömningen som kontroller per byggnad: intervall och instruktion
                        gäller som standard, och kan justeras per byggnad i underhållsplanen. Bedömningen utförs i appen.
                    </p>
                </div>
                {missingGroups.length === 0 && (
                    <Button
                        variant="primary"
                        onClick={saveTemplates}
                        disabled={saving || !isAdmin}
                    >
                        {saving ? 'Sparar...' : 'Spara mallar'}
                    </Button>
                )}
            </div>

            {missingGroups.length > 0 && (
                <Alert variant="warning" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <span>
                        {missingGroups.length === ASSESSMENT_GROUP_KEYS.length
                            ? 'Mallarna för skickbedömning saknas. Skickbedömning schemaläggs inte förrän de finns.'
                            : `${missingGroups.length} av ${ASSESSMENT_GROUP_KEYS.length} mallar för skickbedömning saknas.`}
                        {!isAdmin && ' Kontakta en administratör för att skapa dem.'}
                    </span>
                    {isAdmin && (
                        <Button variant="primary" size="sm" onClick={handleCreateTemplates} disabled={creating}>
                            {creating ? <><Spinner animation="border" size="sm" className="me-1" />Skapar...</> : 'Skapa mallar'}
                        </Button>
                    )}
                </Alert>
            )}

            <Row>
                {ASSESSMENT_GROUP_KEYS.map(group => {
                    const definition = ASSESSMENT_GROUPS[group];
                    const template: BuildingCheckTemplate | undefined = templates[group];
                    const edit = edits[group];
                    const interval = edit?.interval ?? DEFAULT_ASSESSMENT_INTERVAL;
                    const description = edit?.description ?? '';
                    const editable = !!template && !!edit && isAdmin;

                    return (
                        <Col key={group} lg={12} xl={4} className="mb-4">
                            <Card className="h-100">
                                <Card.Body>
                                    <h6 className="mb-1">{definition.label}</h6>
                                    <p className="text-muted small mb-3">{definition.membersDescription}</p>

                                    {!template && (
                                        <div className="alert alert-light border small py-2">
                                            Mall saknas – visar standardvärden
                                        </div>
                                    )}

                                    <Form>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small text-muted">Intervall</Form.Label>
                                            <Row>
                                                <Col xs={5}>
                                                    <NumberInput
                                                        className="form-control"
                                                        min="1"
                                                        integer
                                                        emptyValue={1}
                                                        value={interval.value}
                                                        disabled={!editable}
                                                        onChange={(rawValue) => updateEdit(group, {
                                                            interval: { ...interval, value: Math.max(1, rawValue) }
                                                        })}
                                                    />
                                                </Col>
                                                <Col xs={7}>
                                                    <Form.Select
                                                        value={interval.unit}
                                                        disabled={!editable}
                                                        onChange={(e) => updateEdit(group, {
                                                            interval: { ...interval, unit: e.target.value as DateIntervalUnit }
                                                        })}
                                                    >
                                                        {INTERVAL_UNITS.map(unit => (
                                                            <option key={unit.value} value={unit.value}>
                                                                {unit.label}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                            </Row>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="small text-muted">Instruktion</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                value={description}
                                                disabled={!editable}
                                                onChange={(e) => updateEdit(group, { description: e.target.value })}
                                                placeholder="Skriv instruktioner för skickbedömning..."
                                            />
                                        </Form.Group>

                                        <div className="alert alert-info small mb-0 py-2">
                                            <strong>Intervall:</strong> Var {interval.value} {INTERVAL_UNITS.find(u => u.value === interval.unit)?.label.toLowerCase()}
                                            {template && <span className="text-muted"> · mall {template.id}</span>}
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

export default ConditionAssessmentTab;
